<template>
  <section class="journey-actions">
    <header class="journey-actions__header">
      <div class="journey-actions__heading">
        <h2 class="journey-actions__title">
          {{ $t('journey.actions.title') }}
          <span class="journey-actions__count">{{ scopedActions.length }}</span>
        </h2>
        <p class="journey-actions__description">{{ $t('journey.actions.description') }}</p>
      </div>
      <div class="journey-actions__scopes" role="tablist">
        <button
          v-for="option in scopeOptions"
          :key="option.value"
          type="button"
          role="tab"
          class="journey-actions__scope"
          :class="{ 'journey-actions__scope--active': scope === option.value }"
          :aria-selected="scope === option.value"
          @click="scope = option.value"
        >
          {{ option.label }}
          <span class="journey-actions__scope-count">{{ option.count }}</span>
        </button>
      </div>
    </header>

    <p v-if="loading && !actions.length" class="journey-actions__state">
      {{ $t('common.loading') }}
    </p>
    <p v-else-if="!scopedActions.length" class="journey-actions__state">
      {{ $t('journey.actions.empty') }}
    </p>

    <div v-else class="journey-actions__sections">
      <div v-for="section in sections" :key="section.key" class="journey-actions__section">
        <h3 v-if="section.label" class="journey-actions__section-title">
          {{ section.label }}
          <span class="journey-actions__count">{{ section.count }}</span>
        </h3>
        <p v-if="!section.groups.length" class="journey-actions__state">
          {{ section.empty }}
        </p>
        <div v-else class="journey-actions__groups">
          <div v-for="group in section.groups" :key="group.key" class="journey-actions__group">
            <div class="journey-actions__group-header">
              <span class="journey-actions__group-label">{{ group.label }}</span>
              <span class="journey-actions__group-count">{{ group.items.length }}</span>
            </div>

            <div
              v-for="deal in group.visible"
              :key="deal.id"
              role="button"
              tabindex="0"
              class="journey-actions__row"
              @click="openDeal(deal)"
              @keydown.enter.self="openDeal(deal)"
            >
              <DealOwnerPicker
                :owner="deal.owner"
                :owner-id="deal.owner_id || null"
                :members="members"
                :show-label="section.showAssignLabel && !deal.owner_id"
                @select="(ownerId) => assignOwner(deal, ownerId)"
              />
              <span class="journey-actions__name">{{ dealName(deal) }}</span>
              <span class="journey-actions__phase">
                {{ $t(`journey.phases.${deal.journey.phase}`) }}
              </span>
              <span class="journey-actions__value">{{ formatCurrency(deal.value) }}</span>
              <span
                class="journey-actions__age"
                :class="{ 'journey-actions__age--long': waitingDays(deal) > LONG_WAIT_DAYS }"
              >
                {{ waitingDays(deal) > 0 ? $t('deals.age_days', { count: waitingDays(deal) }) : '' }}
              </span>
            </div>

            <button
              v-if="group.items.length > PREVIEW_SIZE"
              type="button"
              class="journey-actions__toggle"
              @click="toggleGroup(group.key)"
            >
              {{
                group.items.length > group.visible.length
                  ? $t('journey.actions.show_all', { count: group.items.length })
                  : $t('journey.actions.show_less')
              }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
  import { ref, computed, onMounted, getCurrentInstance } from 'vue';
  import { useRouter } from 'vue-router';
  import { dealService } from '@/services/dealService';
  import { useDealStore } from '@/store/dealStore';
  import { useToastStore } from '@/store/toastStore';
  import { useUserStore } from '@/store/userStore';
  import { journeyStepLabel, journeyWaitingDays } from '@/utils/journey';
  import DealOwnerPicker from '@/components/deals/DealOwnerPicker.vue';

  const PREVIEW_SIZE = 3;
  const LONG_WAIT_DAYS = 14;

  // Most urgent first: money and running work before new sales.
  const STEP_ORDER = [
    'send_payment_reminder',
    'send_invoice',
    'create_final_invoice',
    'complete_project',
    'schedule_work_order',
    'create_work_order',
    'place_order',
    'start_preparation',
    'start_project',
    'schedule_measurement',
    'create_measurement',
    'create_definitive_proposal',
    'send_proposal',
    'follow_up_proposal',
    'create_proposal',
    'renew_proposal',
  ];

  export default {
    name: 'JourneyActionsPanel',

    components: { DealOwnerPicker },

    props: {
      members: {
        type: Array,
        default: () => [],
      },
    },

    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const router = useRouter();
      const dealStore = useDealStore();
      const toastStore = useToastStore();
      const userStore = useUserStore();

      const actions = ref([]);
      const loading = ref(false);
      const scope = ref('inbox');
      const expanded = ref({});

      const currentUserId = computed(() => userStore.user?.id || userStore.userData?.id || null);

      const fetchActions = async () => {
        loading.value = true;
        try {
          const data = await dealService.getJourneyActions();
          actions.value = data.actions || [];
        } catch (error) {
          toastStore.addToast({ type: 'error', message: $t('tasks_view.load_failed') });
        } finally {
          loading.value = false;
        }
      };

      onMounted(fetchActions);

      const inScope = {
        inbox: (deal) => !deal.owner_id || deal.owner_id === currentUserId.value,
        mine: (deal) => Boolean(deal.owner_id) && deal.owner_id === currentUserId.value,
        unassigned: (deal) => !deal.owner_id,
        everyone: () => true,
      };

      const scopeOptions = computed(() =>
        ['inbox', 'unassigned', 'mine', 'everyone'].map((value) => ({
          value,
          label: $t(`journey.actions.${value}`),
          count: actions.value.filter(inScope[value]).length,
        }))
      );

      const scopedActions = computed(() => actions.value.filter(inScope[scope.value]));

      const waitingDays = (deal) => journeyWaitingDays(deal.journey.waiting_since);

      const stepRank = (key) => {
        const index = STEP_ORDER.indexOf(key);
        return index === -1 ? STEP_ORDER.length : index;
      };

      const buildGroups = (deals, keyPrefix) => {
        const byKey = new Map();
        for (const deal of deals) {
          const step = deal.journey.next_step;
          const key = `${keyPrefix}:${step.key}:${step.proposal_type || ''}`;
          if (!byKey.has(key)) {
            byKey.set(key, {
              key,
              stepKey: step.key,
              label: journeyStepLabel($t, step),
              items: [],
            });
          }
          byKey.get(key).items.push(deal);
        }

        return [...byKey.values()]
          .sort((a, b) => stepRank(a.stepKey) - stepRank(b.stepKey))
          .map((group) => {
            const items = [...group.items].sort((a, b) => waitingDays(b) - waitingDays(a));
            return {
              ...group,
              items,
              visible: expanded.value[group.key] ? items : items.slice(0, PREVIEW_SIZE),
            };
          });
      };

      const sections = computed(() => {
        if (scope.value === 'inbox') {
          const mine = actions.value.filter(inScope.mine);
          const unassigned = actions.value.filter(inScope.unassigned);
          return [
            {
              key: 'mine',
              label: $t('journey.actions.mine'),
              count: mine.length,
              empty: $t('journey.actions.mine_empty'),
              showAssignLabel: false,
              groups: buildGroups(mine, 'mine'),
            },
            {
              key: 'unassigned',
              label: $t('journey.actions.unassigned'),
              count: unassigned.length,
              empty: $t('journey.actions.unassigned_empty'),
              showAssignLabel: false,
              groups: buildGroups(unassigned, 'unassigned'),
            },
          ];
        }

        return [
          {
            key: scope.value,
            label: null,
            count: scopedActions.value.length,
            empty: $t('journey.actions.empty'),
            showAssignLabel: scope.value === 'everyone',
            groups: buildGroups(scopedActions.value, scope.value),
          },
        ];
      });

      const toggleGroup = (key) => {
        expanded.value = { ...expanded.value, [key]: !expanded.value[key] };
      };

      const openDeal = (deal) => {
        router.push({ name: 'DealDetail', params: { id: deal.id } });
      };

      const assignOwner = async (deal, ownerId) => {
        try {
          await dealStore.updateDeal(deal.id, { owner_id: ownerId });
          const member = props.members.find((item) => {
            const user = item.user || item;
            return (user?.id ?? item.user_id) === ownerId;
          });
          const user = member ? member.user || member : null;
          deal.owner_id = ownerId;
          deal.owner = user
            ? { id: ownerId, first_name: user.first_name, last_name: user.last_name }
            : null;
          toastStore.addToast({ type: 'success', message: $t('deals.deal_updated') });
        } catch (error) {
          toastStore.addToast({ type: 'error', message: $t('deals.update_failed') });
        }
      };

      const dealName = (deal) => {
        const lead = deal.lead;
        const name = lead
          ? [lead.first_name, lead.last_name].filter(Boolean).join(' ') ||
            lead.name ||
            lead.company_name
          : null;
        return name || deal.title || $t('deals.untitled_deal');
      };

      const formatCurrency = (value) =>
        new Intl.NumberFormat('nl-NL', {
          style: 'currency',
          currency: 'EUR',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(value || 0);

      return {
        PREVIEW_SIZE,
        LONG_WAIT_DAYS,
        actions,
        loading,
        scope,
        scopeOptions,
        scopedActions,
        sections,
        toggleGroup,
        openDeal,
        assignOwner,
        dealName,
        waitingDays,
        formatCurrency,
      };
    },
  };
</script>

<style scoped lang="scss">
  .journey-actions {
    padding: 16px;
    border: 1px solid #e3e6ee;
    border-radius: 6px;
    background: #fff;

    &__header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 16px;
      margin-bottom: 12px;

      @include lg-down {
        flex-direction: column;
      }
    }

    &__title {
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 0;
      color: #000921;
      font-size: 15px;
      font-weight: 500;
    }

    &__count {
      color: #8a93a8;
      font-size: 13px;
      font-weight: 400;
    }

    &__description {
      margin: 4px 0 0;
      color: #434f6f;
      font-size: 13px;
      font-weight: 300;
    }

    &__scopes {
      display: inline-flex;
      flex-shrink: 0;
      flex-wrap: wrap;
      padding: 3px;
      border: 1px solid #d7d7d7;
      border-radius: 4px;
    }

    &__scope {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 4px 8px;
      border: 0;
      border-radius: 4px;
      background: transparent;
      color: #434f6f;
      font: inherit;
      font-size: 13px;
      line-height: 20px;
      cursor: pointer;

      @include hover {
        background: rgb(0 9 33 / 4%);
      }

      &--active {
        background: #e3e6ee;
        color: #000921;
      }
    }

    &__scope-count {
      color: #8a93a8;
      font-size: 11px;
      font-weight: 500;
    }

    &__state {
      margin: 0;
      padding: 16px 0 4px;
      color: #434f6f;
      font-size: 13px;
      font-weight: 300;
    }

    &__sections {
      display: flex;
      flex-direction: column;
      gap: 20px;
    }

    &__section-title {
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 0 0 8px;
      color: #000921;
      font-size: 13px;
      font-weight: 500;
    }

    &__groups {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
      gap: 12px;

      @include lg-down {
        grid-template-columns: 1fr;
      }
    }

    &__group {
      display: flex;
      flex-direction: column;
      gap: 2px;
      padding: 10px;
      border-radius: 6px;
      background: #f6f7fa;
    }

    &__group-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      padding: 2px 4px 6px;
    }

    &__group-label {
      color: #000921;
      font-size: 13px;
      font-weight: 500;
      @include ellipsis(1);
    }

    &__group-count {
      flex-shrink: 0;
      color: #434f6f;
      font-size: 13px;
      font-weight: 300;
    }

    &__row {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 8px 10px;
      border: 1px solid #e3e6ee;
      border-radius: 4px;
      background: #fff;
      cursor: pointer;

      @include hover {
        border-color: #d7d7d7;
      }

      &:focus-visible {
        outline: 2px solid #3364e6;
        outline-offset: 2px;
      }
    }

    &__name {
      flex: 1;
      min-width: 0;
      color: #000921;
      font-size: 13px;
      font-weight: 500;
      @include ellipsis(1);
    }

    &__phase,
    &__value,
    &__age {
      flex-shrink: 0;
      color: #434f6f;
      font-size: 11px;
      font-weight: 300;
    }

    &__age {
      min-width: 32px;
      text-align: right;

      &--long {
        color: #ff9500;
        font-weight: 500;
      }
    }

    &__toggle {
      align-self: flex-start;
      margin-top: 4px;
      padding: 2px 4px;
      border: 0;
      background: transparent;
      color: #3364e6;
      font: inherit;
      font-size: 12px;
      cursor: pointer;
    }
  }
</style>
